"use client";

import React, { JSX } from "react";

import MarketingLink from "@/components/common/LinkHandler/MarketingLink";
import { Logo } from "@/components/icons/Logo";
import { TextElementNova } from "@/components/ui-kit/TextElementNova/TextElementNova";
import { GA4 } from "@/utils/ga4Utils";
import { cn } from "@/utils/general";
import { prepareLink } from "@/utils/prepareLink";
import { ThemeTypes } from "@/utils/theming";

import { AdChoicesLink } from "./AdChoicesLink";
import { data, FooterLink, menData, OverallFooterData, SocialItem } from "./data";
import { EmailSignup } from "./EmailSignup";
import styles from "./Footer.module.scss";
import { FooterLogin } from "./FooterLogin";

interface LinkListProps {
  list: FooterLink[];
  theme: ThemeTypes;
}

const LinkList: React.FC<LinkListProps> = ({ list, theme = "master" }) => {
  return (
    <>
      {list.map((el: FooterLink, idx: number) => {
        if (el.link === "adchoices") {
          const cls = process.env.NEXT_PUBLIC_RENDER_CSP === "1" ? "block" : "hidden";
          return (
            <div className={cn(styles.link, cls)} key={`${el.link}-${idx}`}>
              <AdChoicesLink isLightTheme={false} />
            </div>
          );
        }

        return (
          <div
            className={cn(
              styles.link,
              el.link.length > 16 && styles.longLink,
              el.title.toLowerCase().replace(/\s/g, "-")
            )}
            key={`${el.link}-${idx}`}
          >
            <MarketingLink path={prepareLink(el.link, theme)} module_location={`${theme}_footer`}>
              <TextElementNova element="span" className="Body-10-Dia-Normal" text={el.title} />
            </MarketingLink>
          </div>
        );
      })}
    </>
  );
};

const buildEmailForm = (type: string, theme: ThemeTypes): JSX.Element => {
  return (
    <>
      <EmailSignup data={data} type={type} theme={theme} />
      <FooterLogin type={type} theme={theme} />
    </>
  );
};

type FooterProps = {
  theme?: ThemeTypes;
};

export const Footer: React.FC<FooterProps> = ({ theme = "master" }) => {
  const menTheme = theme === "men";
  const handleSocialClick = (el: SocialItem): void => {
    GA4.onOutboundLinkClicked({
      destination: el.link,
      module_location: `${theme}_footer`,
      text: "",
      type: "icon",
    });
  };

  const typedData: OverallFooterData = menTheme ? menData : data;

  return (
    <footer
      className={cn(
        styles.footerContainer,
        "w-full",
        menTheme ? "bg-nova-brand-darkblue-100" : "bg-nova-base-black"
      )}
      data-mousedown
    >
      <div className="w-full max-w-[1600px] mx-auto px-6 md:px-8 lg:px-20 py-12 lg:py-20">
        {/* Mobile only start */}
        <div className="block md:hidden">
          <div className="w-full">
            <div className={cn(styles.logoWrap, "mb-8")}>
              <Logo />
            </div>

            <section className={cn(styles.keep, "mb-8 md:mb-10 w-full p-0")}>
              <TextElementNova
                element="h2"
                className="Body-10-Dia-Strong text-nova-base-white mb-4 md:mb-2"
                text={typedData.keep.title}
              />

              <TextElementNova
                className="Body-10-Dia-Normal text-nova-transparency-white-60"
                text={typedData.keep.text}
              />
            </section>

            <section className={cn(styles.patent, "mb-8 md:mb-5 lg:mb-0 w-full p-0")}>
              <TextElementNova
                element="h2"
                className="Body-10-Dia-Strong text-nova-base-white mb-4 md:mb-2"
                text={typedData.patent.title}
              />
              <TextElementNova
                className="Body-10-Dia-Normal patent text-nova-transparency-white-60"
                text={typedData.patent.text}
              />
            </section>
          </div>

          <div className="flex flex-nowrap flex-row mb-8">
            <div className="col-wellness w-1/2 md:w-full lg:w-[20%] 2xl:w-[14%]">
              <TextElementNova
                element="h3"
                className="Caption-10-Dia-Uppercase text-nova-transparency-white-60 mb-4 lg:mb-6"
                text="Hair Wellness"
              />
              <LinkList list={typedData.wellness} theme={theme} />
            </div>

            <div className="col-company w-1/2 pl-4 md:pl-0 lg:w-[20%] 2xl:w-[14%]">
              <TextElementNova
                element="h3"
                className="Caption-10-Dia-Uppercase text-nova-transparency-white-60 mb-4 lg:mb-6"
                text="Company"
              />
              <LinkList list={typedData.company} theme={theme} />
            </div>

            <div className={cn(styles.styledLinks, "w-1/3")}>
              <TextElementNova
                element="h3"
                className="Caption-10-Dia-Uppercase text-nova-transparency-white-60 mb-4 lg:mb-6"
                text="Legal"
              />
              <LinkList list={typedData.policy} theme={theme} />
            </div>
          </div>

          <div className={cn(styles.styledMobileLinks, "flex flex-nowrap flex-row mb-8")}>
            <div className={cn(styles.colLegalX, "w-full lg:hidden")}>
              <TextElementNova
                element="h3"
                className="Caption-10-Dia-Uppercase text-nova-transparency-white-60 mb-4 lg:mb-6"
                text="Legal"
              />
              <LinkList list={typedData.policy} theme={theme} />
            </div>
          </div>

          {buildEmailForm("email-m", theme)}

          <div className="relative text-nova-transparency-white-60 my-8 w-full">
            <TextElementNova
              className="Caption-10-Dia-Normal text-nova-transparency-white-60 w-full"
              text={typedData.copy.text}
            />
          </div>

          <div
            className={cn(
              styles.styledSocial,
              "flex flex-row justify-between bg-nova-transparency-white-10 rounded w-64 h-16 p-0 px-3 items-center md:h-14 md:mt-auto md:mb-auto lg:w-60 lg:px-5 lg:mb-0"
            )}
          >
            {typedData.social.map((el: SocialItem, idx: number) => {
              const Image = el.img;
              return (
                <a
                  href={el.link}
                  key={`${el.link}-${idx}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`flex justify-center items-center w-[25%] h-full`}
                  aria-label={`visit our ${el.title} page (Opens in new tab)`}
                  onClick={() => handleSocialClick(el)}
                >
                  <Image alt={el.title} />
                </a>
              );
            })}
          </div>
        </div>
        {/* Mobile only end */}

        {/* Desktop only start */}
        <div className="hidden md:flex flex-col max-w-full">
          <div className="flex flex-wrap py-10 md:py-0 max-w-full">
            <div className="w-full md:w-[44%] md:mr-[12%] lg:w-[31.5%] lg:mr-[7.5%]">
              <div className={cn(styles.logoWrap, "mb-8")}>
                <Logo />
              </div>

              <section className={cn(styles.keep, "mb-8 md:mb-10 w-full p-0")}>
                <TextElementNova
                  element="h2"
                  className="Body-10-Dia-Strong text-nova-base-white mb-4 md:mb-2"
                  text={typedData.keep.title}
                />

                <TextElementNova
                  className="Body-10-Dia-Normal text-nova-transparency-white-60"
                  text={typedData.keep.text}
                />
              </section>

              <section
                className={cn(styles.patent, "mb-8 md:mb-[19px] lg:mb-5 xl:mb-0 w-full p-0")}
              >
                <TextElementNova
                  element="h2"
                  className="Body-10-Dia-Strong text-nova-base-white mb-4 md:mb-2"
                  text={typedData.patent.title}
                />
                <TextElementNova
                  className="Body-10-Dia-Normal lg:pr-8 text-nova-transparency-white-60"
                  text={typedData.patent.text}
                />
              </section>

              <div className={cn(styles.tabletEmailForm, "w-full xl:hidden")}>
                {buildEmailForm("email-l", theme)}
              </div>
            </div>

            <div className="pt-[74px] lg:pt-0 w-1/2 md:w-[44%] lg:grid-cols-12 lg:w-[61%]">
              <div className="flex flex-col w-full md:grid md:grid-cols-[135px_1fr] md:gap-y-12 md:gap-x-6 lg:flex lg:flex-row lg:gap-x-12 xl:gap-x-16 2xl:gap-x-24">
                <div className="col-wellness w-1/2 md:w-full lg:w-[20%] 2xl:w-[14%]">
                  <TextElementNova
                    element="h3"
                    className="w-full Caption-10-Dia-Uppercase text-nova-transparency-white-60 mb-4 lg:mb-6"
                    text="Hair Wellness"
                  />
                  <LinkList list={typedData.wellness} theme={theme} />
                </div>

                <div className="col-company w-1/2 pl-4 md:pl-0 lg:w-[20%] 2xl:w-[14%]">
                  <TextElementNova
                    element="h3"
                    className="w-full Caption-10-Dia-Uppercase text-nova-transparency-white-60 mb-4 lg:mb-6"
                    text="Company"
                  />
                  <LinkList list={typedData.company} theme={theme} />
                </div>

                <div
                  className={cn(
                    styles.styledLinks,
                    styles.colLegalL,
                    "w-full md:flex md:flex-wrap md:self-start md:flex-1"
                  )}
                >
                  <TextElementNova
                    element="h3"
                    className="w-full Caption-10-Dia-Uppercase text-nova-transparency-white-60 mb-4 lg:mb-6"
                    text="Legal"
                  />

                  <div
                    className={cn(
                      styles.linkGrid,
                      "grid grid-cols-[1fr_1fr] gap-x-12 lg:flex lg:flex-col"
                    )}
                  >
                    <LinkList list={typedData.policy} theme={theme} />
                  </div>
                </div>
              </div>
            </div>

            <div className="w-full mt-20 text-left md:mt-4 md:items-center lg:mt-8 lg:flex-wrap xl:mt-10 flex">
              <div className="relative text-nova-transparency-white-60 mb-8 w-full md:mb-0 md:w-1/2 md:mr-[5.7%] lg:w-[31%] lg:mr-[8%] lg:self-end">
                <TextElementNova
                  className="Caption-10-Dia-Normal text-nova-transparency-white-60 w-full"
                  text={typedData.copy.text}
                />
              </div>
              <div className="md:w-68 lg:flex lg:w-[61%] lg:gap-x-24 xl:gap-x-32 2xl:gap-x-24">
                <div className="self-center lg:w-[40%] lg:self-end">
                  <div
                    className={cn(
                      styles.styledSocial,
                      "flex flex-row justify-between bg-nova-transparency-white-10 rounded w-60 h-16 px-3 items-center md:h-14 md:mt-auto md:w-64 md:mb-0 lg:w-60 lg:px-5 "
                    )}
                  >
                    {typedData.social.map((el: SocialItem, idx: number) => {
                      const Image = el.img;
                      return (
                        <a
                          href={el.link}
                          key={`${el.link}-${idx}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => handleSocialClick(el)}
                          aria-label={`Visit our ${el.title} page (Opens in new tab)`}
                          className="flex justify-center items-center w-[25%] h-full"
                        >
                          <Image alt={el.title} />
                        </a>
                      );
                    })}
                  </div>
                </div>

                <div className="hidden lg:block flex-1">{buildEmailForm("email-xl", theme)}</div>
              </div>
            </div>
          </div>
        </div>
        {/* Desktop only end */}
      </div>
    </footer>
  );
};
